import React, { useState } from 'react';
import {
  FDBodyText, FDBox, FDDeleteButton,
  FDIcon, FDSecondaryActionIconButton, FDStack, FDTitleText,
} from '@facet-wealth/design';
import PropTypes from 'prop-types';
import useMobileLayout from 'Hooks/useMobileLayout';
import fr from 'facet-rest';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';

const useStyles = buildUseStyles('GoalItem', theme => ({
  stats: {
    display: 'flex',
    columnGap: theme.spacing(2), // Using FDStack, you can only set gap, and that messes things up when you wrap onto a newline
    flexWrap: 'wrap',
  },
}));

const GoalItem = ({
  resource,
  isGrouped = false,
  reprioritizing = false,
  onDelete = undefined,
}) => {
  const classes = useStyles();
  const { loadModalCollectionByLink } = useModalContext();
  const [loading, setLoading] = useState(false);
  const isMobile = useMobileLayout();

  const handleDelete = async () => {
    setLoading(true);
    if (onDelete) {
      await onDelete(resource);
    }
  };

  const canDelete = resource.meta().canDelete() && onDelete && !isGrouped;
  const canEdit = resource.withRelationship(fr.relationships.edit.name,
    () => true,
    () => false);

  if (isMobile) {
    return (
      <FDStack direction={'row'} spacing={2} width={'100%'}>
        <FDStack direction={'column'} width={'100%'}>
          <FDStack minWidth={48} justifyContent={'center'} alignItems={'center'}>
            <FDIcon icon={resource.attr('icon')} color={'textSecondary'} />
          </FDStack>
          <FDStack direction={'column'} spacing={1} width={'100%'}>
            <FDTitleText size={'lg'} value={resource.attr('title')} />
            <FDStack direction={'column'} width={'100%'}>
              {resource.attr('stats').map(stat => (
                <FDBodyText key={stat} value={stat} />
              ))}
            </FDStack>
          </FDStack>
        </FDStack>
        {!reprioritizing && (
          <FDStack direction={'column'} justifyContent={'center'} alignItems={'center'}>
            <FDSecondaryActionIconButton
              size={'sm'}
              icon={'edit'}
              loading={loading}
              disabled={!canEdit}
              onClick={async () => loadModalCollectionByLink(resource.relationship(fr.relationships.edit.name).link('related').url())}
            />
            <FDDeleteButton
              size={'sm'}
              appearance={'secondary'}
              onClick={handleDelete}
              loading={loading}
              disabled={!canDelete}
            />
          </FDStack>
        )}
      </FDStack>
    );
  }

  return (
    <FDStack direction={'row'} width={'100%'} alignItems={'center'} paddingX={2} spacing={2} minHeight={80}>
      <FDStack justifyContent={'center'} minWidth={48}>
        <FDIcon icon={resource.attr('icon')} color={'textSecondary'} />
      </FDStack>
      <FDStack direction={'column'} justifyContent={'center'}>
        <FDTitleText size={'lg'} value={resource.attr('title')} />
        <div className={classes.stats}>
          {resource.attr('stats').map(stat => (
            <FDBodyText key={stat} value={stat} maxLines={1} />
          ))}
        </div>
      </FDStack>
      {/* Spacer box */}
      <FDBox flexGrow={1} />
      {!reprioritizing && (
        <FDStack direction={'row'}>
          <FDSecondaryActionIconButton
            size={'sm'}
            icon={'edit'}
            loading={loading}
            disabled={!canEdit}
            onClick={async () => loadModalCollectionByLink(resource.relationship(fr.relationships.edit.name).link('related').url())}
          />
          <FDDeleteButton
            size={'sm'}
            appearance={'secondary'}
            onClick={handleDelete}
            loading={loading}
            disabled={!canDelete}
          />
        </FDStack>
      )}
    </FDStack>
  );
};

GoalItem.propTypes = {
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
  isGrouped: PropTypes.bool,
  reprioritizing: PropTypes.bool,
  onDelete: PropTypes.func,
};

export { GoalItem };
